"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { useAnimate, stagger } from "motion/react";
import cx from "classnames";

import RainbowButton from "@/components/RainbowButton";
import Link from "@/components/Link";
import Icon from "@ide/shared/components/Icon";
import SocialMediaList from "@/components/SocialMediaList";

const staggerMenuItems = stagger(0.03, { startDelay: 0.2 });

function useMenuAnimation(isOpen: boolean) {
  const [scope, animate] = useAnimate();

  React.useEffect(() => {
    animate("li", isOpen ? { opacity: 1 } : { opacity: 0 }, {
      duration: 0.35,
      delay: isOpen ? staggerMenuItems : 0,
    });
  }, [isOpen]);

  return scope;
}

const Item = ({ children, href, onClick }) => (
  <li className="py-2.5">
    <Link href={href} className="text-2xl font-semibold" onClick={onClick}>
      {children}
    </Link>
  </li>
);

const Menu = ({ onLinkClick = () => {}, isOpen = false, theme = "dark" }) => {
  const t = useTranslations();
  const scope = useMenuAnimation(isOpen);

  return (
    <div
      className={cx("max-w-root mx-auto flex flex-col", {
        "text-white": theme === "dark",
      })}
    >
      <div className="h-12"></div>

      <ul className="my-4" ref={scope}>
        {/* <Item href="/listings" onClick={onLinkClick}>
          Listings
        </Item> */}
        <Item href="/map" onClick={onLinkClick}>
          {t("common.header.nav.menu.projects_map")}
        </Item>
        <Item href="/projects/prelaunch" onClick={onLinkClick}>
          {t("common.header.nav.menu.prelaunching_projects")}
        </Item>
        <Item href="/areas" onClick={onLinkClick}>
          {t("common.header.nav.menu.areas")}
        </Item>
        <Item href="/why-dubai" onClick={onLinkClick}>
          {t("common.header.nav.menu.why_dubai")}
        </Item>
        <Item href="/uae-visa-comparison" onClick={onLinkClick}>
          {t("common.header.nav.menu.visa_comparison")}
        </Item>
        <Item href="/insights" onClick={onLinkClick}>
          {t("common.header.nav.menu.insights")}
        </Item>
        <Item href="/developers" onClick={onLinkClick}>
          {t("common.header.nav.menu.developers")}
        </Item>
        <Item href="/affiliate" onClick={onLinkClick}>
          {t("common.header.nav.menu.affiliate_program")}
        </Item>
      </ul>

      {process.env.NEXT_PUBLIC_SHOW_WHATSAPP_NUMBER === "true" && (
        <>
          <div className="h-8"></div>

          <div className="flex">
            <RainbowButton
              as="a"
              href="tel:+971567330486"
              // className="flex-1 pl-5 pr-3 py-1.5 rounded-full bg-white text-left outline-hidden text-gray-500 shadow-lg"
              className="btn-rainbow flex items-center h-12 px-6 py-1.5 rounded-full outline-hidden shadow-lg"
            >
              <Icon icon="fa-solid fa-phone" className="mr-2 shadow-lg" />
              {t("marketing.call_to_action.call_phone", { number: "+971 56 773 0486" })}
            </RainbowButton>
          </div>
        </>
      )}

      <div className="h-24"></div>

      {process.env.NEXT_PUBLIC_SHOW_WHATSAPP_NUMBER === "true" && (
        <div className="flex flex-col items-center gap-4">
          <div>
            <p className="text-sm text-gray-600 font-medium">
              {t("marketing.call_to_action.social_media_hint")}
            </p>

            <div className="h-4"></div>

            <div className="max-w-72 mx-auto">
              <SocialMediaList />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu;
